import { SQLFilters } from '../../../../api/types';
import { RegularMetricId } from '../../../../api/types-graphql';
import {
  ChartGridConfig,
  DashboardPageOptions,
  LanguageString,
  PageConfig,
} from '../../../../common/components/dashboards/types';
import { TimePeriodSegmentedVizViews } from '../../../../common/components/data-viz-containers/time-period-segmented/types';
import { TimePeriodUnsegmentedVizViews } from '../../../../common/components/data-viz-containers/time-period-unsegmented/types';
import {
  StoryboardComponentIds,
  StoryboardItemConfig,
} from '../../../../common/components/storyboard-components/common/types';
import { Languages } from '../../../../constants';
import {
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
  SegmentBy,
  SingleValueTimeSelection,
  TimeSelectionType,
} from '../../../../types';

const pageOptions: DashboardPageOptions = {
  displayOptions: {
    enabled: true,
  },
  timeOptions: {
    timeSliderEnabled: false,
    referenceDate: '2025-03-31',
  },
  filterTray: {
    enabled: false,
  },
  dataView: {
    enabled: false,
  },
  segmentationLevel1: {
    enabled: false,
  },
  segmentationLevel2: {
    enabled: false,
  },
  benchmark: {
    enabled: false,
  },
  forecast: {
    enabled: false,
  },
};

const employmentTypeSegmentedValues = [
  'Regular',
  'Fixed Term Part Time',
  'Indefinitely Part Time',
  'Fixed Term',
];
const customField6SegmentedValues = [null, '3.5 years', '2.5 years'];

export const getByDimensionChartConfig = (
  chartId: string,
  metric: RegularMetricId,
  dimension: DataFieldWithDataType,
  timeSelection: SingleValueTimeSelection,
  title: LanguageString,
  filters: SQLFilters | null,
  segmentation: SegmentBy | null,
  gridConfig: ChartGridConfig
): StoryboardItemConfig => {
  let byDimensionChartConfig: StoryboardItemConfig;
  if (segmentation) {
    byDimensionChartConfig = {
      chartId,
      layoutConfig: {
        ...gridConfig,
      },
      componentConfig: {
        componentId: StoryboardComponentIds.TIME_PERIOD_SEGMENTED,
        dataConfig: {
          title,
          query: {
            metric: {
              type: 'RegularMetricId',
              value: metric,
            },
            filters: filters ?? undefined,
            groupBy: {
              type: 'non-hierarchical',
              dataField: dimension,
            },
            segmentBy: segmentation,
            timeSelection,
          },
        },
        selectedView: TimePeriodSegmentedVizViews.BAR_CHART,
      },
    };
  } else {
    byDimensionChartConfig = {
      chartId,
      layoutConfig: {
        ...gridConfig,
      },
      componentConfig: {
        componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
        dataConfig: {
          title,
          queries: [
            {
              metric: {
                type: 'RegularMetricId',
                value: metric,
              },
            },
          ],
          filters: filters ?? undefined,
          timeSelection,
          groupBy: {
            type: 'non-hierarchical',
            dataField: dimension,
          },
        },
        selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
      },
    };
  }
  return byDimensionChartConfig;
};

export const getEmployeeRatioPageConfig = (dashboardId: string): PageConfig => {
  const getLocationAndEmpTypeChart = (date: string) =>
    getByDimensionChartConfig(
      'headcount-by-location-and-employment-type-chart',
      RegularMetricId.Pm0001_1IndividualsCount,
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.LOCATION_LEVEL_1,
      },
      {
        type: TimeSelectionType.CalendarYearSingleValueByMonths,
        input: { start: date, end: date },
      },
      {
        [Languages.EN]: 'Location and Employment Type',
        [Languages.JA]: '',
      },
      '',
      {
        type: 'non-hierarchical',
        dataField: {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.EMPLOYMENT_TYPE_LEVEL_1,
        },
        filters: employmentTypeSegmentedValues,
      },
      {
        gridRowSpan: 1,
        gridColSpan: 1,
      }
    );
  const getLocationAndCustomField6Chart = (date: string) =>
    getByDimensionChartConfig(
      'headcount-by-location-and-custom-field6-chart',
      RegularMetricId.Pm0001_1IndividualsCount,
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.LOCATION_LEVEL_1,
      },
      {
        type: TimeSelectionType.CalendarYearSingleValueByMonths,
        input: { start: date, end: date },
      },
      {
        [Languages.EN]: 'Location and Custom Field 6',
        [Languages.JA]: '',
      },
      `EMPLOYEE.EMPLOYMENT_TYPE_LEVEL_1 = 'Fixed Term Part Time'`,
      {
        type: 'non-hierarchical',
        dataField: {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.CUSTOM_FIELD_6_LEVEL_1,
        },
        filters: customField6SegmentedValues,
      },
      {
        gridRowSpan: 1,
        gridColSpan: 1,
      }
    );

  const pageConfig: PageConfig = {
    pageId: '1',
    dashboardId,
    pageNumber: 1,
    enabled: true,
    title: {
      [Languages.EN]: 'Human Resources Table®️-Employee Ratio',
      [Languages.JA]: '人材諸表®️-社員割合',
    },
    pageOptions,
    gridCols: 1,
    gridRows: 2,
    configType: 'componentConfigs',
    components: ({ storyboardDateUtils: { nowEndOfMonth } }) => [
      getLocationAndEmpTypeChart(nowEndOfMonth),
      getLocationAndCustomField6Chart(nowEndOfMonth),
    ],
  };
  return pageConfig;
};

export const getWorkingEnvironmentPageConfig = (
  dashboardId: string
): PageConfig => {
  const customField4Filter =
    "EMPLOYEE.CUSTOM_FIELD_4_LEVEL_1 = 'Selling' AND EMPLOYEE.CUSTOM_FIELD_4_LEVEL_2 = 'Retail'";
  const getMonthlyOtChart = (date: string) =>
    getByDimensionChartConfig(
      'monthly-ot-chart',
      RegularMetricId.Pm0215_2MonthlyOtSumPerHcWithOt,
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.CUSTOM_FIELD_4_LEVEL_3,
      },
      {
        type: TimeSelectionType.CalendarYearSingleValueByMonths,
        input: { start: date, end: date },
      },
      {
        [Languages.EN]: 'Monthly OT Avg Per Employee',
        [Languages.JA]: '時間外労働　当該従業員あたり平均',
      },
      customField4Filter,
      null,
      {
        gridRowSpan: 1,
        gridColSpan: 1,
      }
    );
  const getCustomPaidLeaveChart = (date: string) =>
    getByDimensionChartConfig(
      'custom-paid-leave-chart',
      RegularMetricId.Pm0197_1CustomPaidLeave1AhCumSumPercUtilization,
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.CUSTOM_FIELD_4_LEVEL_3,
      },
      {
        type: TimeSelectionType.CalendarYearSingleValueByMonths,
        input: { start: date, end: date },
      },
      {
        [Languages.EN]: 'AH Vacation Utilization Rate',
        [Languages.JA]: 'AH休暇消化率',
      },
      customField4Filter,
      null,
      {
        gridRowSpan: 1,
        gridColSpan: 1,
      }
    );
  const pageConfig: PageConfig = {
    pageId: '2',
    dashboardId,
    pageNumber: 2,
    enabled: true,
    title: {
      [Languages.EN]: 'Human Resources Table®️-Working Environment',
      [Languages.JA]: '人材諸表®️-就労環境',
    },
    pageOptions,
    gridCols: 1,
    gridRows: 2,
    configType: 'componentConfigs',
    components: ({ storyboardDateUtils: { nowEndOfMonth } }) => [
      getMonthlyOtChart(nowEndOfMonth),
      getCustomPaidLeaveChart(nowEndOfMonth),
    ],
  };
  return pageConfig;
};

export const getPatagoniaHumanResourcesPageConfigs = (
  dashboardId: string
): PageConfig[] => {
  const pageConfigs = [
    getEmployeeRatioPageConfig(dashboardId),
    getWorkingEnvironmentPageConfig(dashboardId),
  ];
  return pageConfigs;
};
